import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { UiStore } from '../stores/ui.store';
import { AppStore } from '../stores/app.store';
import { AuthManagerService } from '../managers/auth-manager.service';
import { ChatManagerService } from '../managers/chat-manager.service';
import { NavigationEnd, Router } from '@angular/router';
import {
  RoleManagerService,
  ManageableOrganizationModel,
  ManageableFacilityModel,
} from '../managers/role-manager.service';
import { filter, map } from 'rxjs';
import { IconComponent } from '../shared/components/icon/icon.component';
import { sortByText } from '../shared/utils/sort.utils';
import { isUpsertRoute } from './route-matchers';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [FormsModule, IconComponent],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit {
  protected readonly uiStore = inject(UiStore);
  protected readonly appStore = inject(AppStore);
  private readonly authManager = inject(AuthManagerService);
  private readonly router = inject(Router);
  private readonly roleManager = inject(RoleManagerService);
  protected readonly chatManager = inject(ChatManagerService);

  protected organizations = signal<ManageableOrganizationModel[]>([]);
  private readonly allFacilities = signal<ManageableFacilityModel[]>([]);

  protected readonly sortedOrganizations = computed(() =>
    sortByText(this.organizations(), org => org.name)
  );

  protected readonly facilities = computed(() => {
    const orgId = this.appStore.selectedOrganizationId();

    if (!orgId) {
      return [];
    }

    const orgFacilities = this.allFacilities().filter(f => f.organizationId === orgId);
    const sortedFacilities = sortByText(orgFacilities, facility => facility.name);

    return sortedFacilities;
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  protected readonly isOnUpsertPage = computed(() => isUpsertRoute(this.currentUrl()));

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    this.appStore.loadFromStorage();

    const [orgResult, facResult] = await Promise.all([
      this.roleManager.getManageableOrganizations(),
      this.roleManager.getManageableFacilities(),
    ]);

    this.organizations.set(orgResult.organizations);
    this.allFacilities.set(facResult.facilities);
    this.chatManager.setManageableFacilityIds(facResult.facilities.map((facility) => facility.id));
    this.appStore.setHasAnyFacility(facResult.facilities.length > 0);

    // If previously selected org is no longer accessible, clear it
    const currentOrgId = this.appStore.selectedOrganizationId();

    if (currentOrgId && !orgResult.organizations.some(o => o.id === currentOrgId)) {
      this.appStore.setSelectedOrganizationId(null);
    }

    // If previously selected facility is no longer accessible, clear it
    const currentFacilityId = this.appStore.selectedFacilityId();
    const currentFacility = facResult.facilities.find(f => f.id === currentFacilityId);

    if (currentFacilityId && !currentFacility) {
      this.appStore.setSelectedFacility(null, null);
    } else if (currentFacility) {
      // Refresh the cached timezone in case it changed server-side.
      this.appStore.setSelectedFacility(currentFacility.id, currentFacility.timeZone);
    }

    const manageableFacilities = this.allFacilities();

    if (manageableFacilities.length === 1 && !this.appStore.selectedFacilityId()) {
      const onlyFacility = manageableFacilities[0];

      this.appStore.setSelectedOrganizationId(onlyFacility.organizationId);
      this.appStore.setSelectedFacility(onlyFacility.id, onlyFacility.timeZone);
    }
  }

  protected onOrganizationChange(orgId: string) {
    this.appStore.setSelectedOrganizationId(orgId || null);
  }

  protected onFacilityChange(facilityId: string) {
    if (!facilityId) {
      this.appStore.setSelectedFacility(null, null);

      return;
    }

    const facility = this.allFacilities().find(f => f.id === facilityId);
    const timeZone = facility ? facility.timeZone : null;

    this.appStore.setSelectedFacility(facilityId, timeZone);
  }

  protected toggleSidebar() {
    this.uiStore.toggleSidebar();
  }

  protected toggleMessagesFlyout() {
    this.chatManager.toggleFlyout();
  }

  protected logout() {
    this.authManager.logout();
    void this.router.navigate(['/login']);
  }
}
